import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Signal,
  ViewChild,
  WritableSignal,
  signal,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CloudflareService } from '../../_services/cloudflare.service';
import { Subject, takeUntil } from 'rxjs';
import {
  ApiAsset,
  CreateAssetMetadataInput,
  JsAssetMetadata,
} from '../../_interfaces/others.interface';
import { MatDialog } from '@angular/material/dialog';
import { ViewImageDialogComponent } from '../../shared/view-image-dialog/view-image-dialog.component';
import {
  AssetUploadDialogComponent,
  AssetUploadDialogModel,
} from '../../shared/asset-upload-dialog/asset-upload-dialog.component';
import { environment } from '../../../environments/environment';
import { AlertService } from '../../_services/alert.service';
import { AuthService } from '../../_services/auth.service';
import { FirebaseService } from '../../_services/firebase.service';
import { FirestoreStoreService } from '../../_services/firestore-store.service';
import { DateFnsModule } from 'ngx-date-fns';

@Component({
  selector: 'app-assets',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    MatTooltip,
    DateFnsModule,
  ],
  templateUrl: './assets.component.html',
  styleUrl: './assets.component.scss',
})
export class AssetsComponent {
  @ViewChild('fileInput') fileInput: any;
  @ViewChild('singleFileInput') singleFileInput: any;

  // Inject services
  private store = inject(FirestoreStoreService);
  private cloudflareService = inject(CloudflareService);
  private dialog = inject(MatDialog);
  private alertService = inject(AlertService);
  private snackBar = inject(MatSnackBar);
  private authService = inject(AuthService);
  public fbs = inject(FirebaseService);

  // Use store for assets data - automatically updates in real-time!
  allAssets: Signal<JsAssetMetadata[]> = this.store.activeAssets;

  assetSearch: FormControl = new FormControl('');
  filtersQuery: WritableSignal<string> = signal('');
  unSubscribe = new Subject<void>();
  editingAsset: WritableSignal<JsAssetMetadata | null> = signal(null);
  baseUrl: string = environment.assetsBaseUrl;
  isUploading: WritableSignal<boolean> = signal(false);

  // Computed filtered assets based on search query (client-side search)
  filteredAssets: Signal<JsAssetMetadata[]> = computed(() => {
    const query = this.filtersQuery().toLowerCase().trim();
    const assets = this.allAssets();
    if (!query) return assets;

    return assets.filter((asset) => {
      // Search in displayName
      if (asset.displayName?.toLowerCase().includes(query)) return true;
      // Search in description
      if (asset.description?.toLowerCase().includes(query)) return true;
      // Search in tags
      if (asset.tags?.some((tag) => tag.toLowerCase().includes(query)))
        return true;
      // Search in original filename
      if (asset.originalName?.toLowerCase().includes(query)) return true;
      // Search in public link URL
      if (asset.url?.toLowerCase().includes(query)) return true;
      return false;
    });
  });

  /** Check if current user is admin (can delete assets) */
  get isAdmin(): boolean {
    return this.authService.isAdmin;
  }

  /** Check if assets are loading (from store) */
  isLoading(): boolean {
    return this.store.assetsLoading();
  }

  // No ngOnInit needed - store is automatically initialized by AppComponent!

  /** Clear search input */
  clearSearch(): void {
    this.assetSearch.setValue('');
    this.filtersQuery.set('');
  }

  copyLink(asset: JsAssetMetadata) {
    navigator.clipboard.writeText(asset.url);
    this.snackBar.open('✓ Link copied to clipboard', 'Close', {
      duration: 3000,
      panelClass: ['snackbar-success'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  trackByAsset(index: number, asset: JsAssetMetadata) {
    return asset.id;
  }

  formatFileSize(bytes: string | number): string {
    const size = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
    if (isNaN(size) || size === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /** Get cache-busted URL for an asset (uses updatedAt timestamp to bust browser cache) */
  getCacheBustedUrl(asset: JsAssetMetadata): string {
    const timestamp = asset.updatedAt?.getTime() || asset.createdAt?.getTime() || Date.now();
    return `${asset.url}?v=${timestamp}`;
  }

  viewAsset(asset: JsAssetMetadata) {
    this.dialog.open(ViewImageDialogComponent, {
      data: {
        selectedImageUrl: this.getCacheBustedUrl(asset),
        type: asset.contentType?.split('/')[1] || 'png',
      },
      panelClass: 'image-viewer-dialog',
    });
  }

  // Triggered when file is selected from file input
  onFileSelected(event: any) {
    const files: File[] = Array.from(event.target.files);
    if (files.length === 0) return;

    // Open upload dialog for each file (one at a time for now)
    this.openUploadDialog(files[0]);

    // Reset file input
    event.target.value = '';
  }

  openUploadDialog(file: File) {
    const dialogRef = this.dialog.open(AssetUploadDialogComponent, {
      data: new AssetUploadDialogModel(file),
      width: '100%',
      maxWidth: '550px',
      disableClose: true,
      panelClass: 'asset-upload-dialog-panel',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.uploadAssetWithMetadata(result.file, result.metadata);
      }
    });
  }

  async uploadAssetWithMetadata(
    file: File,
    metadata: CreateAssetMetadataInput
  ) {
    this.isUploading.set(true);

    try {
      // Step 1: Upload file to R2
      const r2Response = await this.cloudflareService
        .uploadAsset(file)
        .toPromise();

      if (!r2Response?.data?.[0]) {
        throw new Error('Failed to upload file to R2');
      }

      const uploadedFile: ApiAsset = r2Response.data[0];

      // Step 2: Create metadata in Firestore
      // The store will automatically pick up this new asset via real-time listener
      await this.cloudflareService.createAssetMetadata(
        uploadedFile.filename,
        uploadedFile.originalName,
        uploadedFile.type,
        uploadedFile.size,
        metadata
      );

      this.snackBar.open(
        `✓ Asset "${metadata.displayName}" uploaded successfully`,
        'Close',
        {
          duration: 5000,
          panelClass: ['snackbar-success'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        }
      );
    } catch (error) {
      console.error('Upload error:', error);
      this.snackBar.open(
        '✗ Failed to upload asset. Please try again.',
        'Close',
        {
          duration: 5000,
          panelClass: ['snackbar-error'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        }
      );
    } finally {
      this.isUploading.set(false);
    }
  }

  replaceAsset(asset: JsAssetMetadata) {
    this.editingAsset.set(asset);
    this.singleFileInput.nativeElement.click();
  }

  downloadAsset(asset: JsAssetMetadata) {
    // Create a temporary link to download the asset
    const link = document.createElement('a');
    link.href = asset.url;
    link.download = asset.originalName || asset.displayName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  editAssetMetadata(asset: JsAssetMetadata) {
    const dialogRef = this.dialog.open(AssetUploadDialogComponent, {
      data: {
        file: { name: asset.originalName, size: asset.size },
        isEdit: true,
        asset: asset,
      },
      width: '100%',
      maxWidth: '550px',
      disableClose: true,
      panelClass: 'asset-edit-dialog-panel',
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result && result.metadata) {
        try {
          // Update Firestore - the store will automatically pick up the changes
          await this.cloudflareService.updateAssetMetadata(
            asset.id,
            result.metadata
          );

          this.snackBar.open('✓ Asset metadata updated successfully', 'Close', {
            duration: 5000,
            panelClass: ['snackbar-success'],
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });
        } catch (error) {
          console.error('Update metadata error:', error);
          this.snackBar.open(
            '✗ Failed to update asset metadata. Please try again.',
            'Close',
            {
              duration: 5000,
              panelClass: ['snackbar-error'],
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
            }
          );
        }
      }
    });
  }

  async onReplaceFile(event: any) {
    const asset = this.editingAsset();
    if (!asset) return;
    const newFile = event.target.files[0];
    if (!newFile) return;

    this.isUploading.set(true);

    this.cloudflareService
      .updateAsset(asset.r2Key, newFile)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe({
        next: async () => {
          // Update Firestore metadata to track who made the update
          // The store will automatically pick up the changes via real-time listener
          await this.cloudflareService.updateAssetMetadataOnReplace(asset.id);

          this.snackBar.open('✓ Asset replaced successfully', 'Close', {
            duration: 5000,
            panelClass: ['snackbar-success'],
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });
          this.editingAsset.set(null);
          this.isUploading.set(false);
          // Reset file input
          event.target.value = '';
        },
        error: (error) => {
          console.error('Replace error:', error);
          this.snackBar.open(
            '✗ Failed to replace asset. Please try again.',
            'Close',
            {
              duration: 5000,
              panelClass: ['snackbar-error'],
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
            }
          );
          this.editingAsset.set(null);
          this.isUploading.set(false);
          event.target.value = '';
        },
      });
  }

  async deleteAsset(asset: JsAssetMetadata) {
    const confirm = await this.alertService.confirm(
      'Delete Asset',
      `Are you sure you want to delete "${asset.displayName}"?`,
      'Yes',
      'No'
    );
    if (!confirm) return;

    try {
      // Delete from Firestore (soft delete)
      // The store will automatically pick up the change and filter out deleted assets
      await this.cloudflareService.deleteAssetMetadata(asset.id);

      // Delete from R2
      await this.cloudflareService.deleteAssetFromR2(asset.r2Key).toPromise();

      this.snackBar.open('✓ Asset deleted successfully', 'Close', {
        duration: 5000,
        panelClass: ['snackbar-success'],
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
    } catch (error) {
      console.error('Delete error:', error);
      this.snackBar.open(
        '✗ Failed to delete asset. Please try again.',
        'Close',
        {
          duration: 5000,
          panelClass: ['snackbar-error'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        }
      );
    }
  }

  ngOnDestroy() {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }
}
